"use client";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Script from "next/script";
import SaveMethodSurface from "@/components/payments/save-method-surface";
import { submitActivePaymentSurface } from "@/lib/payments/payment-surface-registry";
import { PaymentProcessor } from "@/types/auth";
import { RainforestPaymentMethodConfig } from "@/types/rainforest";
import { assignPaymentMethodConfig, createPaymentMethodConfig } from "@/api/provider/billing/payment-terminal";
export interface SavedCard {
  /** The processor's own id - a Rainforest `payment_method_id` or a Stripe `pm_...`. */
  paymentMethodId: string;
  /** Rainforest only; used for the optimistic row while the save is in flight. */
  preview?: {
    brand?: string;
    last4?: string;
  };
}
export interface CreditCardFieldRef {
  /**
   * The form is minted before the patient exists, so on Stripe it is handed
   * to them here first - a card captured without that can't be charged.
   */
  submit: (patientId: string) => Promise<SavedCard>;
  isValid: () => boolean;
}
interface CreditCardFieldProps {
  onValidityChange: (isValid: boolean) => void;
}
const CreditCardField = forwardRef<CreditCardFieldRef, CreditCardFieldProps>(function CreditCardField({
  onValidityChange
}, ref) {
  const [paymentMethodConfig, setPaymentMethodConfig] = useState<RainforestPaymentMethodConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store resolve/reject for the submit promise
  const submitPromiseRef = useRef<{
    resolve: (card: SavedCard) => void;
    reject: (error: string) => void;
  } | null>(null);
  const isValidRef = useRef(false);
  const fetchPaymentMethodConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const config = await createPaymentMethodConfig();
      if (config) {
        setPaymentMethodConfig(config);
      } else {
        setError("Failed to load payment form.");
      }
    } catch (error_0) {
      console.error("Failed to fetch payment method config:", error_0);
      setError("Failed to load payment form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (!paymentMethodConfig && !isLoading) {
      fetchPaymentMethodConfig();
    }
  }, [paymentMethodConfig, isLoading, fetchPaymentMethodConfig]);

  // Expose submit and isValid methods via ref
  useImperativeHandle(ref, () => ({
    submit: async (patientId: string) => {
      if (!paymentMethodConfig) {
        return Promise.reject("Payment form not ready");
      }
      // Stripe only: a Rainforest card is reusable without a customer, so
      // Rainforest clinics save exactly as before.
      if (paymentMethodConfig.processor === PaymentProcessor.STRIPE) {
        await assignPaymentMethodConfig(paymentMethodConfig.paymentMethodConfigId, patientId);
      }
      return new Promise<SavedCard>((resolve, reject) => {
        submitPromiseRef.current = {
          resolve,
          reject
        };
        if (!submitActivePaymentSurface()) {
          reject("Payment form not ready");
          submitPromiseRef.current = null;
        }
      });
    },
    isValid: () => isValidRef.current
  }));

  // The id is what gets vaulted; the card details only fill the row shown
  // while the save is in flight, and only Rainforest hands them over.
  const handleSuccess = (paymentMethodId: string, event?: any) => {
    const card = event?.detail?.[0]?.data?.card;
    if (submitPromiseRef.current) {
      submitPromiseRef.current.resolve({
        paymentMethodId,
        preview: card ? {
          brand: card.brand,
          last4: card.last4
        } : undefined
      });
      submitPromiseRef.current = null;
    }
    isValidRef.current = true;
    onValidityChange(true);
  };
  const handleFailure = (err: string) => {
    setError(err);
    if (submitPromiseRef.current) {
      submitPromiseRef.current.reject(err);
      submitPromiseRef.current = null;
    }
    isValidRef.current = false;
    onValidityChange(false);
  };
  const handleValid = () => {
    isValidRef.current = true;
    onValidityChange(true);
  };
  const handleInvalid = () => {
    isValidRef.current = false;
    onValidityChange(false);
  };
  if (isLoading) {
    return <div className="rounded-lg border border-border bg-background-secondary p-4">
          <p className="text-body text-muted-foreground">
            Loading payment form...
          </p>
        </div>;
  }
  if (error && !paymentMethodConfig) {
    return <div className="rounded-lg border border-status-error-bg bg-status-error-bg p-4">
          <p className="text-body text-status-error-text">{error}</p>
          <button type="button" onClick={fetchPaymentMethodConfig} className="mt-2 text-body-emphasis text-primary">
            Retry
          </button>
        </div>;
  }
  if (!paymentMethodConfig) {
    return null;
  }
  return <div>
        <Script src={paymentMethodConfig.sandbox ? "https://static.rainforestpay.com/sandbox.payment.js" : "https://static.rainforestpay.com/payment.js"} strategy="afterInteractive" />
        <SaveMethodSurface paymentMethodConfig={paymentMethodConfig} onSuccess={handleSuccess} onFailure={handleFailure} onValid={handleValid} onInvalid={handleInvalid} hideButton={true} className="pt-0 [&>div]:pb-4" />
        {error && <p className="mt-2 text-body text-status-error-text">{error}</p>}
      </div>;
});
export default CreditCardField;